import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { TILE, tileKey } from '../../shared/geo.js';
import { resolveCircle } from '../../shared/collision.js';
import { hash2 } from '../../shared/rng.js';

const ZERO = new THREE.Matrix4().makeScale(0, 0, 0);

const CELL = 22; // metres: at most one animal of a kind lives in each cell
const RANGE = 5; // cells drawn in each direction
const MAX = 70; // animals of one kind drawn at once
const BUCKET = 32; // the collision lookup cell of a tile, in metres
const SALT = { goat: 71, fowl: 72, cow: 73, bush: 74 };
const SKY_CELL = 260; // metres: at most one hawk circles over each cell
const HAWKS = 9;
const SHY = 16; // metres at which a bush animal bolts from the player

function colored(geometry, hex) {
  const g = geometry.index ? geometry.toNonIndexed() : geometry;
  const c = new THREE.Color(hex);
  const n = g.attributes.position.count;
  const col = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    col[i * 3] = c.r;
    col[i * 3 + 1] = c.g;
    col[i * 3 + 2] = c.b;
  }
  g.setAttribute('color', new THREE.BufferAttribute(col, 3));
  return g;
}
const box = (w, h, d, hex, x, y, z) => colored(new THREE.BoxGeometry(w, h, d).translate(x, y, z), hex);

// Blocky animals facing +z, feet on the ground.
function shapes() {
  const legs = (hex, w, h, sx, sz) => [box(w, h, w, hex, sx, h / 2, sz), box(w, h, w, hex, -sx, h / 2, sz), box(w, h, w, hex, sx, h / 2, -sz), box(w, h, w, hex, -sx, h / 2, -sz)];
  return {
    goat: mergeGeometries([box(0.32, 0.34, 0.72, '#d8cdb8', 0, 0.56, 0), box(0.22, 0.24, 0.26, '#d8cdb8', 0, 0.78, 0.42), box(0.05, 0.14, 0.05, '#4a3a2c', 0.07, 0.96, 0.4), box(0.05, 0.14, 0.05, '#4a3a2c', -0.07, 0.96, 0.4), box(0.3, 0.1, 0.3, '#6b4a30', 0, 0.7, -0.1), ...legs('#b8ac96', 0.08, 0.4, 0.11, 0.26)]),
    fowl: mergeGeometries([box(0.18, 0.18, 0.26, '#8a4a2a', 0, 0.26, 0), box(0.1, 0.12, 0.1, '#8a4a2a', 0, 0.4, 0.14), box(0.04, 0.05, 0.08, '#d8402f', 0, 0.48, 0.14), box(0.03, 0.03, 0.06, '#e8a23a', 0, 0.4, 0.22), box(0.06, 0.16, 0.1, '#5a3220', 0, 0.36, -0.16), box(0.03, 0.17, 0.03, '#e8a23a', 0.05, 0.085, 0), box(0.03, 0.17, 0.03, '#e8a23a', -0.05, 0.085, 0)]),
    // the long-horned white cattle herded on the hills
    cow: mergeGeometries([box(0.62, 0.66, 1.5, '#ebe6da', 0, 1.02, 0), box(0.4, 0.4, 0.5, '#ebe6da', 0, 1.22, 0.9), box(0.3, 0.2, 0.2, '#3a3028', 0, 1.1, 1.18), box(0.5, 0.24, 0.5, '#d8d0c0', 0, 1.42, 0.3), box(0.7, 0.07, 0.07, '#cfc6b0', 0, 1.46, 0.86), box(0.07, 0.3, 0.07, '#cfc6b0', 0.34, 1.6, 0.86), box(0.07, 0.3, 0.07, '#cfc6b0', -0.34, 1.6, 0.86), ...legs('#d8d0c0', 0.14, 0.7, 0.2, 0.56)]),
    // a duiker, the small shy antelope of the bush
    bush: mergeGeometries([box(0.26, 0.3, 0.66, '#8a5a34', 0, 0.52, 0), box(0.18, 0.2, 0.24, '#8a5a34', 0, 0.72, 0.4), box(0.2, 0.1, 0.5, '#c9a06a', 0, 0.4, 0), box(0.04, 0.12, 0.04, '#2a211a', 0.05, 0.88, 0.36), box(0.04, 0.12, 0.04, '#2a211a', -0.05, 0.88, 0.36), box(0.1, 0.1, 0.08, '#f1eee6', 0, 0.6, -0.36), ...legs('#6f4728', 0.06, 0.4, 0.09, 0.24)]),
  };
}

// A hawk seen from below: body, spread wings and a fanned tail, facing +z.
function hawkShape() {
  return mergeGeometries([box(0.3, 0.16, 0.9, '#4a3526', 0, 0, 0), box(2.6, 0.06, 0.5, '#5a4230', 0, 0.04, 0.08), box(0.5, 0.06, 0.4, '#3a2a1e', 0, 0.02, -0.56), box(0.16, 0.14, 0.2, '#e8dcc0', 0, 0.02, 0.52)]);
}

// The animals around the player, in proportion to what the simulation says the
// nearest settlement has. Goats and fowls stay about the compounds and cattle
// graze on open ground, each pottering around a home spot. Out in the open
// there are duikers, which bolt when anyone comes near, and hawks circle above
// in numbers that follow the bush animals they live on.
export class Animals {
  constructor(scene, heightmap, chunks) {
    this.heightmap = heightmap;
    this.chunks = chunks;
    this.density = { goat: 0, fowl: 0, cow: 0, bush: 0 }; // chance of each kind per cell
    this.hawkChance = 0; // chance that a hawk circles over a cell of sky
    this.hits = [];
    this.dummy = new THREE.Object3D();
    const material = new THREE.MeshLambertMaterial({ vertexColors: true });
    const s = shapes();
    this.kinds = {};
    for (const kind of Object.keys(s)) {
      const mesh = new THREE.InstancedMesh(s[kind], material, MAX);
      mesh.count = 1; // one copy folded to nothing, so the shader compiles while loading
      mesh.setMatrixAt(0, ZERO);
      mesh.castShadow = true;
      mesh.frustumCulled = false;
      scene.add(mesh);
      this.kinds[kind] = { mesh, list: new Map(), refused: new Set() };
    }
    this.hawks = new THREE.InstancedMesh(hawkShape(), material, HAWKS);
    this.hawks.count = 1;
    this.hawks.setMatrixAt(0, ZERO);
    this.hawks.frustumCulled = false;
    scene.add(this.hawks);
  }

  // herd: [people, goats, fowls, cattle, bush animals, their predators] of the
  // nearest settlement, from the server.
  setHerd(herd) {
    const people = Math.max(1, herd[0]);
    // wild animals keep away from crowds: plenty round a village, hardly any in town
    this.density.bush = Math.min(0.1, ((herd[4] || 0) / Math.max(300, people)) * 0.07);
    this.hawkChance = Math.min(0.85, (herd[5] || 0) / 60);
    // animals per person, turned into a chance for each cell
    this.density.goat = Math.min(0.3, (herd[1] / people) * 0.9);
    this.density.fowl = Math.min(0.4, (herd[2] / people) * 0.5);
    this.density.cow = Math.min(0.25, (herd[3] / people) * 2.5);
  }

  // How many of each kind are in sight.
  count() {
    return { goats: this.kinds.goat.mesh.count, fowls: this.kinds.fowl.mesh.count, cattle: this.kinds.cow.mesh.count, duikers: this.kinds.bush.mesh.count, hawks: this.hawks.count };
  }

  // Where an animal of this kind may live in a cell: null while the ground
  // there is still loading, false if it is no place for one, or { x, z }.
  homeFor(kind, gx, gz) {
    const salt = SALT[kind];
    let x = (gx + hash2(gx, gz, salt + 10)) * CELL;
    let z = (gz + hash2(gx, gz, salt + 20)) * CELL;
    const t = this.chunks.tiles.get(tileKey(Math.floor(x / TILE), Math.floor(z / TILE)));
    if (!t || t.state !== 'built') return null;
    if (this.chunks.surfaceAt(x, z) !== -1 || this.chunks.wet(x, z, 1)) return false; // not in the road or the river
    const homes = t.buckets ? t.buckets.get(Math.floor(x / BUCKET) * 100003 + Math.floor(z / BUCKET)) : null;
    // goats and fowls live among the houses; cattle and wild animals away from them
    if (kind === 'goat' || kind === 'fowl' ? !homes : !!homes) return false;
    const fixed = resolveCircle(x, z, 0.8, this.chunks.collidersNear(x, z, this.hits));
    x = fixed.x;
    z = fixed.z;
    if (Math.abs(this.heightmap.normalAt(x, z)[1]) < 0.8) return false; // too steep to stand on
    return { x, z };
  }

  // daylight: 0 at night to 1 by day. time: milliseconds.
  update(dt, x, z, time, daylight) {
    const cx = Math.floor(x / CELL);
    const cz = Math.floor(z / CELL);
    const beat = Math.floor(time / 1000);
    for (const [kind, k] of Object.entries(this.kinds)) {
      const salt = SALT[kind];
      const seen = new Set();
      for (let dz = -RANGE; dz <= RANGE; dz++) {
        for (let dx = -RANGE; dx <= RANGE; dx++) {
          const gx = cx + dx;
          const gz = cz + dz;
          if (hash2(gx, gz, salt) > this.density[kind]) continue;
          const key = gx * 100003 + gz;
          if (k.list.has(key)) {
            seen.add(key);
            continue;
          }
          if (k.refused.has(key) || k.list.size >= MAX) continue;
          const home = this.homeFor(kind, gx, gz);
          if (home === null) continue;
          if (home === false) {
            k.refused.add(key);
            continue;
          }
          seen.add(key);
          k.list.set(key, { hx: home.x, hz: home.z, x: home.x, z: home.z, tx: home.x, tz: home.z, yaw: hash2(gx, gz, salt + 30) * 6.28, pause: hash2(gx, gz, salt + 40) * 8, seed: key });
        }
      }
      if (k.refused.size > 4000) k.refused.clear();
      let i = 0;
      for (const [key, a] of k.list) {
        if (!seen.has(key)) {
          k.list.delete(key);
          continue;
        }
        let speed = kind === 'fowl' ? 0.9 : 0.6;
        if (kind === 'bush') {
          const away = Math.hypot(a.x - x, a.z - z);
          if (away < SHY) {
            // bolt straight away from whoever came too close
            a.tx = a.x + ((a.x - x) / (away || 1)) * 24;
            a.tz = a.z + ((a.z - z) / (away || 1)) * 24;
            a.pause = 0;
            a.bolting = true;
          }
          if (a.bolting) speed = 6;
        }
        // animals sleep where they stand at night
        if (!a.bolting && (a.pause > 0 || daylight < 0.25)) {
          a.pause -= dt;
        } else {
          const ddx = a.tx - a.x;
          const ddz = a.tz - a.z;
          const d = Math.hypot(ddx, ddz);
          if (d < 0.4) {
            a.bolting = false;
            const turn = hash2(a.seed, beat, 5) * 6.28;
            const reach = (kind === 'cow' ? 9 : 6) * hash2(a.seed, beat, 6);
            a.tx = a.hx + Math.cos(turn) * reach;
            a.tz = a.hz + Math.sin(turn) * reach;
            a.pause = 1 + hash2(a.seed, beat, 8) * 7;
          } else {
            a.x += (ddx / d) * speed * dt;
            a.z += (ddz / d) * speed * dt;
            const fixed = resolveCircle(a.x, a.z, 0.4, this.chunks.collidersNear(a.x, a.z, this.hits));
            if (fixed.hit) {
              // a wall in the way: stop here and choose somewhere else next
              a.tx = a.x = fixed.x;
              a.tz = a.z = fixed.z;
            }
            let diff = Math.atan2(ddx, ddz) - a.yaw;
            while (diff > Math.PI) diff -= Math.PI * 2;
            while (diff < -Math.PI) diff += Math.PI * 2;
            a.yaw += diff * Math.min(1, dt * 5);
          }
        }
        this.dummy.position.set(a.x, this.heightmap.heightAt(a.x, a.z), a.z);
        this.dummy.rotation.set(0, a.yaw, 0);
        this.dummy.updateMatrix();
        k.mesh.setMatrixAt(i++, this.dummy.matrix);
      }
      if (i === 0) k.mesh.setMatrixAt(0, ZERO);
      k.mesh.count = Math.max(1, i);
      k.mesh.instanceMatrix.needsUpdate = true;
    }

    // hawks ride the air by day, each wheeling over its own patch of country
    let n = 0;
    if (daylight > 0.5) {
      const sx = Math.floor(x / SKY_CELL);
      const sz = Math.floor(z / SKY_CELL);
      for (let dz = -1; dz <= 1; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          const gx = sx + dx;
          const gz = sz + dz;
          if (hash2(gx, gz, 81) > this.hawkChance) continue;
          const mx = (gx + 0.2 + hash2(gx, gz, 82) * 0.6) * SKY_CELL;
          const mz = (gz + 0.2 + hash2(gx, gz, 83) * 0.6) * SKY_CELL;
          const round = 28 + hash2(gx, gz, 84) * 40;
          const turn = (time / 1000) * (0.16 + hash2(gx, gz, 85) * 0.1) + hash2(gx, gz, 86) * 6.28;
          const hx = mx + Math.cos(turn) * round;
          const hz = mz + Math.sin(turn) * round;
          this.dummy.position.set(hx, this.heightmap.heightAt(mx, mz) + 45 + hash2(gx, gz, 87) * 40, hz);
          // nose along the circle, the inner wing dipped into the turn
          this.dummy.rotation.set(0, -turn, 0.28, 'YXZ');
          this.dummy.updateMatrix();
          this.hawks.setMatrixAt(n++, this.dummy.matrix);
        }
      }
      this.dummy.rotation.set(0, 0, 0, 'XYZ');
    }
    if (n === 0) this.hawks.setMatrixAt(0, ZERO);
    this.hawks.count = Math.max(1, n);
    this.hawks.instanceMatrix.needsUpdate = true;
  }
}
